/*
<purpose>
  The identity implementation: acquires an Entra access token via MSAL for the
  Authorization: Bearer header, derives the display user from the MSAL account, and owns BOTH
  interactive doors (login redirect, logout redirect). Consumed ONLY through the identity.ts
  facade.
</purpose>
<dependencies>@azure/msal-browser via the LAZY getters in authConfig.ts (never construct MSAL at
module load).</dependencies>
<constraints>
  Never-throws contract on every export: failure degrades to "no token"/"no user", REPORTED via
  console.error with the error CODE only — never the token, never the account, never a claim
  value. Same rule as the backend auth anchor: authorization events keep the identity,
  diagnostics carry none.
</constraints>
*/

import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { getMsalInstance, getFaTokenRequest } from './authConfig';
// Type-only import: erased at compile time, so the facade importing this module back is no
// runtime cycle.
import type { CurrentUser } from './identity';

const skipAuth = process.env.REACT_APP_SKIP_AUTH === 'true';

// Why (401 semantics): a 401 from the FA means the token MSAL just handed us was rejected
// server-side (revoked, audience/CA change, clock skew) even though MSAL's own local expiry
// check said it was still good — so the next acquireFaToken() call must not silently hand back
// the same stale cache hit. acquireTokenSilent's own `forceRefresh` flag (network round-trip to
// AAD, not a full interactive redirect) is the correct MSAL-native remedy; the identity facade's
// onAuthRejected() calls it from the 401 call site.
let forceRefreshNextToken = false;
export const invalidateCachedFaToken = (): void => { forceRefreshNextToken = true; };

// Why this exists at all: EVERY failure path in this module returns null by design, so a
// misconfigured or failing tenant produces no thrown error, no rejected promise and no visible
// symptom — the app renders normally and every request goes out unauthenticated. The backend
// half of this system fails LOUDLY (it refuses to start without its auth configuration); this
// is the closest the frontend can get without breaking the never-throw contract its callers
// depend on. Diagnosing a mute tenant previously meant reading the code to discover there was
// nothing to read.
//
// PII: the error CODE only. Never the token, never the account, never a claim value —
// identity is not needed in order to say that acquisition failed.
const errorCodeOf = (err: unknown): string => {
  if (typeof err === 'object' && err !== null && 'errorCode' in err) {
    const code = (err as { errorCode?: unknown }).errorCode;
    if (typeof code === 'string' && code.length > 0) return code;
  }
  return err instanceof Error ? err.name : 'unknown';
};

// Serves every reported stage in this module, acquisition and logout alike — so the wording names
// the STAGE rather than assuming one. The PII rule above applies to all of them.
const reportAuthFailure = (stage: string, err: unknown): void => {
  // eslint-disable-next-line no-console
  console.error(`[auth] ${stage} failed: ${errorCodeOf(err)}`);
};

// Why: acquireTokenRedirect itself can reject (most commonly BrowserAuthError
// interaction_in_progress — a redirect already pending, or a concurrent caller). Both call
// sites in acquireFaToken() route through here so neither relies on an outer try/catch to
// stay within the "never throw to the caller" contract — this is provably self-contained.
const triggerInteractiveRedirect = async (): Promise<void> => {
  try {
    // redirectStartPage: MSAL returns to its configured redirectUri (app root) by default,
    // which loses the deep link; naming the current page is what preserves return-where-you-were.
    await getMsalInstance().acquireTokenRedirect({
      ...getFaTokenRequest(),
      redirectStartPage: window.location.href,
    });
  } catch (err) {
    // Still swallowed — the caller already treats this invocation as "no token", and a
    // redirect that fails to start is still "no token". But it is REPORTED: a redirect
    // that never fires is indistinguishable from one still in flight, and that ambiguity is
    // exactly what makes a broken tenant look like a slow one.
    reportAuthFailure('interactive redirect', err);
  }
};

// Why: silent-first, redirect-on-demand — msal-browser v5 popup flow was ruled out (design
// review), so any interaction requirement (no cached token, expired refresh, consent/CA changes)
// falls through to a full-page redirect instead of a popup. The redirect navigates away, so
// the caller of this specific invocation gets null; the resumed app picks up the token via
// handleRedirectPromise() in index.tsx on return. The ENTIRE body — including the account
// lookup, not just acquireTokenSilent — is inside the try: getActiveAccount/getAllAccounts
// are synchronous MSAL calls and a throw from either must degrade to "no token" too, so this
// function is provably never-throws end to end. REACT_APP_SKIP_AUTH=true is the local-dev
// switch: no Entra env configured, so short-circuit before touching MSAL at all.
// The ONE place interactive auth begins when no session exists yet: the Anmeldung click (and
// the 401 recovery in the identity facade). Everything else in this module stays silent-only.
export const beginMsalLogin = async (): Promise<void> => {
  if (skipAuth) return;
  await triggerInteractiveRedirect();
};

// The way OUT, and the counterpart of beginMsalLogin. Why it must go through MSAL rather than a
// platform logout URL: the session this app actually holds is the MSAL account cache in
// sessionStorage. Clearing a cookie somewhere else leaves that cache intact, so the next silent
// acquisition succeeds and "Abmelden" followed by a reload lands the same user straight back in —
// signed out in appearance only. logoutRedirect() removes the account and then performs the
// end-session round trip.
//
// postLogoutRedirectUri is window.location.origin, NOT a relative '/': registered SPA redirect URIs
// are bare origins (measured on a live tenant app registration — no trailing slash, and no separate
// web.logoutUrl), and Entra matches post-logout URIs against them byte-for-byte. MSAL resolves a
// relative '/' to `https://host/`, whose trailing slash does not match, and Entra then refuses the
// return leg. This is the same string authConfig hands MSAL as the LOGIN redirectUri, so both doors
// land on one registered value.
// `account` is named explicitly: with no active account MSAL cannot tell WHICH account to end the
// session for, and silently degrades to clearing nothing.
//
// Never-throws, same contract as every other export here: a logout that fails to start is still
// REPORTED (an Abmelden click that does nothing is indistinguishable from a slow one) but never
// rejects into the caller's click handler.
export const beginMsalLogout = async (): Promise<void> => {
  if (skipAuth) return;

  try {
    const msal = getMsalInstance();
    await msal.logoutRedirect({
      account: msal.getActiveAccount() ?? undefined,
      postLogoutRedirectUri: window.location.origin,
    });
  } catch (err) {
    reportAuthFailure('logout', err);
  }
};

export const acquireFaToken = async (): Promise<string | null> => {
  if (skipAuth) return null;

  try {
    const msal = getMsalInstance();
    const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
    // Why NO interactive redirect here (measured 2026-08-14 on a fresh incognito load): this
    // function runs inside the request interceptor of EVERY API call, including the mount-time
    // anonymous /config fetch — a redirect from here fires before the Anmeldung screen can be
    // used and bypasses the branded entry entirely. No account means "not signed in yet", not
    // "start signing in": the request goes out bare (anonymous endpoints answer; protected ones
    // 401 into the designed stale-session recovery). Interaction is owned by beginMsalLogin —
    // the Anmeldung click — alone.
    if (!account) return null;

    const forceRefresh = forceRefreshNextToken;
    forceRefreshNextToken = false;
    const result = await msal.acquireTokenSilent({ ...getFaTokenRequest(), account, forceRefresh });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Not reported: this is the DESIGNED path — silent renewal could not complete, so we
      // escalate to an interactive redirect. Logging it would make the normal case noisy and
      // train readers to ignore the channel that carries the abnormal one.
      await triggerInteractiveRedirect();
      return null;
    }
    // Why: never throw to the caller — an unexpected MSAL/network failure (including the
    // account lookup itself) degrades to "no token" rather than an unhandled rejection
    // surfacing mid-request. This is the branch that was completely mute before the
    // diagnostics: a wrong audience, a missing pre-authorization or absent build
    // configuration all land HERE.
    reportAuthFailure('silent token acquisition', err);
    return null;
  }
};

// Why: no principal-endpoint round trip — the display-only user shape derives straight
// from the MSAL account already held by the instance, no network call at all. App.tsx consumes
// it as-is through the identity facade.
export const fetchMsalUser = async (): Promise<CurrentUser | null> => {
  if (skipAuth) return null;

  const msal = getMsalInstance();
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (!account) return null;

  // An account with NO username must resolve to "not signed in", never to a user whose empty
  // address then renders "Ihr Konto () ist angemeldet…" on the no-role screen — the address is
  // the one piece of information that makes that screen actionable, so a user object without
  // one is worse than no user object.
  if (!account.username) return null;

  return {
    email: account.username,
    name: (account.idTokenClaims?.name as string | undefined) ?? '',
    userId: account.localAccountId,
  };
};
